import React, { useCallback, useState } from 'react'
import { Box, TableCell, TableRow, useTheme } from '@mui/material'
import { KiotExpandViewportBorderPortal } from './KiotExpandViewportBorderPortal'
import { kiotExpandClosedFrameShellSx, KIOT_EXPAND_PANEL_CLASS } from './kiotListExpandTokens'

export type KiotListExpandPanelProps = {
  colSpan: number
  isExpanded: boolean
  /** Vùng scroll bảng — portal viền viewport. */
  scrollContainerEl?: HTMLDivElement | null
  /** Hàng dữ liệu đang mở — mép trên viền khung. */
  anchorRowEl?: HTMLTableRowElement | null
  /** Chiều rộng viewport scroll — expand content sticky theo vùng nhìn thấy. */
  panelVisibleWidthPx?: number
  /** Chiều cao panel = phần còn lại dưới data row tới đáy vùng scroll. */
  panelMaxHeightPx?: number
  /** @deprecated Không còn dùng overlap shell — giữ tương thích API. */
  dataRowOverlapPx?: number
  minTableWidthPx?: number
  children: React.ReactNode
  className?: string
}

/**
 * Panel expand dưới hàng dữ liệu.
 * Ôm nội dung; trần = phần viewport còn lại dưới data row. Nội dung dài scroll trong detail tabs.
 */
const KiotListExpandPanel: React.FC<KiotListExpandPanelProps> = ({
  colSpan,
  isExpanded,
  scrollContainerEl,
  anchorRowEl,
  panelVisibleWidthPx,
  panelMaxHeightPx,
  dataRowOverlapPx,
  minTableWidthPx,
  children,
  className = KIOT_EXPAND_PANEL_CLASS,
}) => {
  const theme = useTheme()
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null)
  const shellRef = useCallback((el: HTMLDivElement | null) => setContentEl(el), [])

  return (
    <TableRow
      className={className}
      hover={false}
      data-expanded={isExpanded ? 'true' : 'false'}
      sx={{
        position: 'relative',
        overflow: isExpanded ? 'visible' : 'hidden',
        ...(!isExpanded ? { visibility: 'collapse', height: 0 } : {}),
      }}
    >
      <TableCell
        colSpan={colSpan}
        sx={{
          py: '0 !important',
          width: '100%',
          minWidth: minTableWidthPx != null ? `${minTableWidthPx}px` : undefined,
          verticalAlign: 'top',
          border: 'none !important',
          borderRadius: 0,
          backgroundColor: 'transparent',
          overflow: isExpanded ? 'visible' : 'hidden',
          boxSizing: 'border-box',
          p: 0,
          lineHeight: 0,
          height: isExpanded ? 'auto' : undefined,
          maxHeight: isExpanded
            ? panelMaxHeightPx != null && panelMaxHeightPx > 0
              ? panelMaxHeightPx
              : 'var(--kiot-expand-panel-max-h, none)'
            : undefined,
        }}
      >
        <KiotExpandViewportBorderPortal
          active={isExpanded}
          scrollContainer={scrollContainerEl ?? null}
          anchorRowEl={anchorRowEl ?? null}
          contentEl={contentEl}
          widthPx={panelVisibleWidthPx}
        />
        <Box
          ref={shellRef}
          sx={kiotExpandClosedFrameShellSx(
            theme,
            isExpanded,
            panelVisibleWidthPx,
            dataRowOverlapPx,
            panelMaxHeightPx,
          )}
        >
          {isExpanded ? (
            <Box
              onClick={(e) => e.stopPropagation()}
              role="presentation"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                width: '100%',
                minWidth: 0,
                minHeight: 0,
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '100%',
                boxSizing: 'border-box',
                bgcolor: '#fff',
                borderRadius: 0,
                overflow: 'hidden',
                overscrollBehavior: 'contain',
                lineHeight: 'normal',
                '& > *': {
                  flex: '1 1 auto',
                  minHeight: 0,
                  maxHeight: '100%',
                },
              }}
              data-kiot-expand-body-scroll
            >
              {children}
            </Box>
          ) : null}
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default KiotListExpandPanel
